<template>
    <div>
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
        
        <b-table
            :items="contactList"
            :fields="columns"
            :busy="loading"
            class="no-top-border td-align-middle"
        >
            <template #cell(full_name)="data">
                <div class="d-flex gap-4 flex-wrap align-items-center">
                    <div class="">
                        <div class="font-weight-bold">{{ data.value }}</div>
                        <div class="text-muted small">{{ data.item.email }}</div>
                    </div>
                    <div class="">
                        <b-badge 
                            v-if="getSourcingRole(data.item)" 
                            variant="soft-primary"
                            v-b-tooltip.hover.top="'Default Sourcing Contact'" 
                            class="text-uppercase"
                        >{{ getSourcingRole(data.item).name }}</b-badge>
                    </div>
                </div>
                
            </template>
            <template #cell(portal_access)="data">
                <tu-portal-access-badge :portal-access="data.value"></tu-portal-access-badge>
            </template>
            <template #cell(is_invited)="data">
                <div class="d-flex justify-content-center align-items-center">
                    <div class="chip" :class="data.value ? 'chip-success' : 'chip-secondary'">
                        <b-icon :icon="data.value ? 'check-circle-fill' : 'x-circle-fill'" class=""></b-icon>
                        {{ data.value ? 'Yes' : 'No' }}
                    </div>
                    <div>
                        <b-button v-if="data.value && canRemoveFromEvent" key="isInvited" size="sm" variant="bare-secondary" class="text-uppercase font-size-70 font-weight-400" @click="startRemoveInvitedTu(data.item)">Remove?</b-button>
                        <b-button 
                            v-else-if="canAddToEvent" 
                            key="notInvited" 
                            size="sm" 
                            :disabled="data.item.unassignable"
                            variant="bare-primary" 
                            class="text-uppercase font-size-70 font-weight-400"
                            @click="startAddInvitedTu(data.item)"
                        >
                            Add?
                        </b-button>
                    </div>
                </div>
            </template>
        </b-table>

        <div>
            <b-button 
                v-if="canAddContact" 
                variant="subtle-secondary" 
                size="sm" 
                @click="showAddContact"
            >
                <b-icon icon="plus-circle" variant="success" class="mr-2"></b-icon>
                Add New Contact
            </b-button>
        </div>

        <b-modal
            v-model="addContactModal"
            centered
            content-class="bg-gray-100"
            body-class="p-4"
            hide-header
            hide-footer
        >
            <div>
                <h4>Add New Contact</h4>
                <p class="text-muted mb-1">Add a new new contact in this company, and invite them to participate in this sourcing event.</p>
                <p class="text-muted">This will automatically send them an invitation to the sourcing event.</p>
            </div>
            <hr>
            <tenant-user-form
                @cancel="addContactModal = false"
                @submit="handleAddContactSubmit"
            ></tenant-user-form>
        </b-modal>
        
        <!-- <div>
            <h6>contactList</h6>
            <pre>{{ contactList }}</pre>
            <h6>invitedTus</h6>
            <pre>{{ invitedTus }}</pre>
        </div> -->
    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
import { errorHandling } from '../../js/custom-vue-mixins';
import TenantUserForm from '../tenant_users/TenantUserForm';
import { SOURCING_TENANT_ROLE_SLUG, findTenantRole } from '../../js/utils/tenantRoleUtils';

export default {
    name: 'RfxResponseManageInvitedTus',
    components: {
        TenantUserForm,
    },
    mixins: [
        errorHandling,
    ],
    props: {
        responseId: {
            type: [String, Number],
            required: true,
        }
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            addContactModal: false,  // v-model with the modal for adding new contacts
            loading: false,
            contactList: [],
            invitedTus: [],  // list of IDs for invited TUs, full objects will be in contactList
            // various 'perms' from 'metadata' dict in API response - set these once you get API response, all default to false
            canAddToEvent: false,
            canRemoveFromEvent: false,
            canAddContact: false,
            columns: [
                {
                    key: 'full_name',
                    label: 'Name',
                    tdClass: 'font-weight-bold',
                },
                {
                    key: 'portal_access',
                    label: 'Supplier Portal Access',
                    class: 'text-center',
                },
                {
                    key: 'is_invited',
                    label: 'Access To This Event?',
                    class: 'text-center max-width-120',
                },
            ]
        }
    },
    computed: {
    },
    mounted: function() {
        this.getCompanyContacts();
    },
    methods: {
        getSourcingRole(tuObj) {
            // the built-in sourcing TenantRole if this TU holds it (show its name), otherwise undefined
            return findTenantRole(tuObj, SOURCING_TENANT_ROLE_SLUG);
        },
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        setLocalData: function(apiData) {
            // transform API data and set local data
            // we will add .is_invited Boolean to each contact based on whether their ID is in invited_tus
            this.invitedTus = apiData.invited_tus;
            this.contactList = apiData.contacts.map(tu => {
                let contact = {...tu};  // copy the TU
                contact['is_invited'] = apiData.invited_tus.includes(tu.id);  // set the new Boolean
                return contact;
            });
            let metadata = apiData.metadata;
            this.canAddToEvent = metadata ? metadata.can_add_to_event : false;
            this.canRemoveFromEvent = metadata ? metadata.can_remove_from_event : false;
            this.canAddContact = metadata ? metadata.can_add_contact : false;
        },
        getCompanyContacts: function() {
            this.loading = true;
            return suppliosApi.rfxResponse.getCompanyContacts(this.responseId)
                .then(response => {
                    this.setLocalData(response.data);
                    return true;
                })
                .catch(error => {
                    this.apiErrorHandler(error)
                    return false;
                })
                .then(success => {
                    this.loading = false;
                    return success;
                });
        },
        startAddInvitedTu: function(tuObj) {
            // show modal confirmation, then trigger real thing to add TU
            let confirmMsg = `This will send an invitation email to ${tuObj.full_name}, grant them access to this sourcing event, and add them to vendor communications about this event.`;
            this.$bvModal.msgBoxConfirm(confirmMsg, {
                title: 'Invite to Event?',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'primary',
                okTitle: 'Yes, Invite',
                cancelTitle: 'No, Cancel',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
                })
                .then(confirmResult => {
                    if (confirmResult) {
                        this.addInvitedTu(tuObj);
                    }
                })
        },
        addInvitedTu: function(tuObj) {
            // add an EXISTING TU to the event
            this.loading = true;
            let tuId = tuObj.id;
            suppliosApi.rfxResponse.addInvitedTu(this.responseId, tuId) 
                .then(() => {
                    // if that was successful, just reload the list, which will set loading = false
                    this.getCompanyContacts();
                })
                .catch(error => {
                    this.apiErrorHandler(error);
                    this.loading = false;
                })
        },
        startRemoveInvitedTu: function(tuObj) {
            // show modal confirmation, then trigger real thing to remove TU
            let confirmMsg = `Are you sure you want to remove ${tuObj.full_name} from this sourcing event?  This will not affect their overall ability to login to the Supplier Portal.`;
            this.$bvModal.msgBoxConfirm(confirmMsg, {
                title: 'Remove from Event?',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'Yes, Remove',
                cancelTitle: 'No, Cancel',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
                })
                .then(confirmResult => {
                    if (confirmResult) {
                        this.removeInvitedTu(tuObj);
                    }
                })
        },
        removeInvitedTu: function(tuObj) {
            // remove this TU from the event
            this.loading = true;
            let tuId = tuObj.id;
            suppliosApi.rfxResponse.removeInvitedTu(this.responseId, tuId)
                .then(() => {
                    // if that was successful, then just reload the list, which will set loading = false
                    this.getCompanyContacts();
                })
                .catch(error => {
                    this.apiErrorHandler(error);
                    this.loading = false;
                })
        },
        showAddContact: function() {
            // show the modal for adding new contacts
            this.addContactModal = true;
        },
        handleAddContactSubmit: function(tuData) {
            this.loading = true;
            console.log('From handleAddContactSubmit, TU payload is below:');
            console.log(tuData);  // we don't need company in tuData... our backend endpoint adds it
            // tuData must have 'fname', 'lname', 'is_company_admin', '_email', other stuff optional
            return suppliosApi.rfxResponse.createAndAddInvitedTu(this.responseId, tuData)
                .then(response => {
                    // this endpoint creates the new TU, and adds them to invited_tus, returns data on the new TU
                    // show a toast and trigger reload of the list
                    let successMsg = `Successfully added ${response.data.full_name} and invited them to this event.`;
                    this.$toast.success(successMsg);
                    this.getCompanyContacts();  // reload the list, let this set this.loading = false
                    return true;
                })
                .catch(error => {
                    this.apiErrorHandler(error);
                    this.loading = false;
                    return false;
                })
                .then(success => {
                    this.addContactModal = false;
                    return success;
                })
        },
    }
}
</script>

<style>

</style>